import { ClipboardList, CalendarDays, TrendingUp, TrendingDown, Minus, AlertCircle, CheckCircle2, Trophy, Sparkles, Inbox, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return s; // biarkan teks tanggal dari PDF (mis. "KAMIS, 13 AGUSTUS 2026")
  return d.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
};

const gradeColor = (g) => {
  const x = (g || "").toUpperCase();
  if (x.includes("A")) return "bg-[#4A6B53]/12 text-[#4A6B53]";
  if (x.includes("B")) return "bg-[#F4B942]/20 text-[#8a6d16]";
  if (x.includes("C")) return "bg-[#E25E3E]/12 text-[#E25E3E]";
  return "bg-[#D9381E]/10 text-[#D9381E]";
};

export function BagianHeader({ no, title, desc }) {
  return (
    <div className="mt-4 mb-5" data-testid={`bagian-header-${no}`}>
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-full bg-[#1A1C1A] text-white grid place-items-center font-heading font-black shrink-0">{no}</span>
        <h2 className="font-heading font-black text-2xl sm:text-[28px] text-[#1A1C1A]">{title}</h2>
      </div>
      {desc && <p className="text-sm text-[#5C5F5C] mt-2 ml-12 max-w-3xl">{desc}</p>}
    </div>
  );
}

export function EmptyBox({ label, hint }) {
  return (
    <div className="clay-card p-8 text-center border-dashed" data-testid="empty-box">
      <Inbox className="h-9 w-9 text-[#C7C2B8] mx-auto mb-3" />
      <p className="font-heading font-bold text-[#1A1C1A]">{label}</p>
      {hint && <p className="text-sm text-[#9C9A94] mt-1 max-w-md mx-auto">{hint}</p>}
    </div>
  );
}

function AnalisaCard({ title, children }) {
  return (
    <div className="clay-card p-6 mb-4 bg-[#1A1C1A] text-white border-[#1A1C1A]" data-testid="analisa-card">
      <div className="flex items-center gap-2 text-white/80 text-sm mb-2"><Sparkles className="h-4 w-4 text-[#F4B942]" /> {title}</div>
      {children}
    </div>
  );
}

export function RecognizeSection({ report, analisa, forParent = false }) {
  if (!report?.entries?.length) {
    return <EmptyBox label="Belum ada data Recognize" hint="Diagnostik awal (baseline) belum diunggah admin. Bagian ini menampilkan posisi awal kemampuan siswa." />;
  }
  const entries = report.entries;
  return (
    <div data-testid="recognize-section">
      {analisa && (
        <AnalisaCard title="Posisi Awal">
          <p className="text-white/90 leading-relaxed">{analisa.posisi_awal}</p>
          {analisa.perhatian?.length > 0 && (
            <div className="mt-3">
              <p className="text-[#F4B942] text-sm font-semibold mb-1.5 flex items-center gap-1.5"><AlertCircle className="h-4 w-4" /> Perlu perhatian sejak awal</p>
              <ul className="space-y-1">{analisa.perhatian.map((t, i) => <li key={i} className="text-sm text-white/85 flex items-start gap-2"><span className="text-[#F4B942] mt-0.5">→</span> {t}</li>)}</ul>
            </div>
          )}
        </AnalisaCard>
      )}
      <div className="clay-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading font-bold text-[#1A1C1A] flex items-center gap-2"><ClipboardList className="h-4 w-4 text-[#E25E3E]" /> Hasil Diagnostik Awal</h4>
          {report.tanggal && <span className="text-xs text-[#5C5F5C]">{report.tanggal}</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-[#5C5F5C] text-left border-b border-[#E5E0D8]">
              <th className="py-1.5 pr-2">Mapel</th><th className="py-1.5 px-2 text-center">Benar</th>
              <th className="py-1.5 px-2 text-center">Soal</th><th className="py-1.5 px-2 text-center">Skor</th><th className="py-1.5 px-2 text-center">Grade</th>
            </tr></thead>
            <tbody>
              {entries.map((e, i) => {
                const isTotal = (e.mapel || "").toUpperCase() === "TOTAL";
                return (
                  <tr key={i} className={isTotal ? "font-bold border-t border-[#E5E0D8]" : ""}>
                    <td className="py-1.5 pr-2 text-[#1A1C1A]">{e.mapel}</td>
                    <td className="py-1.5 px-2 text-center">{e.benar ?? "-"}</td>
                    <td className="py-1.5 px-2 text-center text-[#5C5F5C]">{e.jumlah_soal ?? "-"}</td>
                    <td className="py-1.5 px-2 text-center text-[#E25E3E] font-medium">{e.skor != null ? `${e.skor}%` : "-"}</td>
                    <td className="py-1.5 px-2 text-center">{e.grade ? <span className={`text-xs px-2 py-0.5 rounded-full ${gradeColor(e.grade)}`}>{e.grade}</span> : "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Delta = ({ pre, post }) => {
  if (pre == null || post == null) return null;
  const d = post - pre;
  if (d > 0) return <span className="inline-flex items-center gap-0.5 text-[#4A6B53] text-xs font-semibold"><TrendingUp className="h-3.5 w-3.5" /> +{d}</span>;
  if (d < 0) return <span className="inline-flex items-center gap-0.5 text-[#D9381E] text-xs font-semibold"><TrendingDown className="h-3.5 w-3.5" /> {d}</span>;
  return <span className="inline-flex items-center gap-0.5 text-[#9C9A94] text-xs font-semibold"><Minus className="h-3.5 w-3.5" /> 0</span>;
};

const PostesTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg bg-[#1A1C1A] text-white text-xs p-2.5 shadow-lg max-w-[220px]">
      <p className="font-semibold mb-0.5">{d.mapel || d.name}</p>
      {d.tanggal && <p className="text-white/60 mb-1">{d.tanggal}</p>}
      {d.Skor != null && <p className="text-[#F4B942]">Skor: <b>{d.Skor}</b></p>}
      {d.Benar != null && <p>Benar: <b>{d.Benar}</b></p>}
      {d.Salah != null && <p>Salah: <b>{d.Salah}</b></p>}
    </div>
  );
};

// 9 mata pelajaran/subtes baku (postes). Semua variasi nama dipetakan ke salah satu ini
// sehingga grafik hanya menampilkan 9 jenis. Urutan array = urutan tampil grafik.
export const CANON_MAPEL = [
  "Penalaran Matematika (PM)",
  "Penalaran Umum (PU)",
  "Pengetahuan Kuantitatif (PK)",
  "Pengetahuan dan Pemahaman Umum (PPU)",
  "Pemahaman Bacaan dan Menulis (PBM)",
  "Literasi dalam Bahasa Indonesia (LBI)",
  "Literasi dalam Bahasa Inggris (LBE)",
  "Literasi Bindo-Saintek (LBK)",
  "Literasi Bindo-Soshum (LBS)",
];

// Petakan nama mapel apa pun (termasuk singkatan & varian) ke salah satu dari 9 baku.
export function canonicalMapel(raw) {
  const m = String(raw || "").toUpperCase();
  const code = (c) => new RegExp("(^|[^A-Z])" + c + "([^A-Z]|$)").test(m);
  if (m.includes("KUANTITATIF") || code("PK")) return "Pengetahuan Kuantitatif (PK)";
  if (m.includes("MATEMATIK") || code("PM")) return "Penalaran Matematika (PM)";
  if (m.includes("SAINTEK") || code("LBK")) return "Literasi Bindo-Saintek (LBK)";
  if (m.includes("SOSHUM") || code("LBS")) return "Literasi Bindo-Soshum (LBS)";
  if ((m.includes("LITERASI") && (m.includes("INGGRIS") || m.includes("ENGLISH"))) || code("LBE")) return "Literasi dalam Bahasa Inggris (LBE)";
  if ((m.includes("LITERASI") && m.includes("INDONESIA")) || code("LBI")) return "Literasi dalam Bahasa Indonesia (LBI)";
  if (m.includes("BACAAN") || m.includes("MENULIS") || code("PBM")) return "Pemahaman Bacaan dan Menulis (PBM)";
  if ((m.includes("PENGETAHUAN") && m.includes("PEMAHAMAN")) || code("PPU")) return "Pengetahuan dan Pemahaman Umum (PPU)";
  if ((m.includes("PENALARAN") && m.includes("UMUM")) || m.includes("INDUKTIF") || m.includes("DEDUKTIF") || code("PU")) return "Penalaran Umum (PU)";
  // Tak dikenali: bersihkan nomor sesi di akhir agar tetap mengelompok
  return String(raw || "Lainnya").replace(/\s*[-#]?\s*(sesi|ke|bagian|part)?\s*[-.]?\s*\d+\s*$/i, "").trim() || "Lainnya";
}

function PostesBarCharts({ items }) {
  if (!items?.length) return null;
  // Kelompokkan per mata pelajaran baku; items sudah urut per tanggal (lama → baru)
  const groups = {};
  items.forEach((it) => {
    const key = canonicalMapel(it.mapel);
    (groups[key] = groups[key] || []).push(it);
  });
  // Tampilkan HANYA mapel yang sudah ada datanya, urut sesuai 9 baku (+ tak dikenal di akhir)
  const present = new Set(Object.keys(groups));
  const ordered9 = CANON_MAPEL.filter((m) => present.has(m));
  const extra = Object.keys(groups).filter((k) => !CANON_MAPEL.includes(k)).sort((a, b) => a.localeCompare(b));
  const mapels = [...ordered9, ...extra];
  return (
    <div className="clay-card p-5 mb-4" data-testid="posttest-trend-chart">
      <h4 className="font-heading font-bold text-[#1A1C1A] mb-1 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#E25E3E]" /> Grafik Skor Postes per Mata Pelajaran</h4>
      <p className="text-xs text-[#5C5F5C] mb-4">Satu grafik per mata pelajaran; tiap sesi (1, 2, 3, …) ditampilkan berdampingan agar terlihat perkembangannya.</p>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-6">
        {mapels.map((mp) => {
          const rows = groups[mp] || []; // sudah urut per tanggal (lama → baru) dari parent
          const data = rows.map((it, i) => ({
            name: `Sesi ${i + 1}`,
            tanggal: it.tanggal,
            mapel: mp,
            Skor: it.skor != null ? it.skor : it.posttest,
            Benar: it.benar,
            Salah: it.salah,
          }));
          return (
            <div key={mp} data-testid={`postes-bar-${mp}`}>
              <p className="font-heading font-bold text-sm text-[#1A1C1A] mb-2 truncate" title={mp}>{mp}</p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data} margin={{ top: 8, right: 6, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0ECE4" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "#5C5F5C", fontSize: 10 }} interval={0} />
                  <YAxis domain={[0, 100]} tick={{ fill: "#5C5F5C", fontSize: 10 }} />
                  <Tooltip content={<PostesTooltip />} cursor={{ fill: "rgba(26,28,26,0.04)" }} />
                  <Bar dataKey="Skor" fill="#E25E3E" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PosttestSection({ items, analisa, forParent = false }) {
  if (!items?.length) {
    return <EmptyBox label="Belum ada data Postes Harian" hint="Hasil postes harian belum diunggah admin. Bagian ini menampilkan progres belajar per tanggal." />;
  }
  const ordered = [...items].reverse(); // terbaru dulu
  return (
    <div data-testid="posttest-section">
      {analisa && items.length >= 2 && (
        <AnalisaCard title="Tren Perkembangan">
          <p className="text-white/90 leading-relaxed">{analisa.tren}</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-3">
            {analisa.membaik?.length > 0 && (
              <div>
                <p className="text-[#4A6B53] text-sm font-semibold mb-1.5 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Progres</p>
                <ul className="space-y-1">{analisa.membaik.map((t, i) => <li key={i} className="text-sm text-white/85 flex items-start gap-2"><span className="text-[#4A6B53] mt-0.5">✓</span> {t}</li>)}</ul>
              </div>
            )}
            {analisa.perlu_perbaikan?.length > 0 && (
              <div>
                <p className="text-[#F4B942] text-sm font-semibold mb-1.5 flex items-center gap-1.5"><AlertCircle className="h-4 w-4" /> Perlu diperbaiki</p>
                <ul className="space-y-1">{analisa.perlu_perbaikan.map((t, i) => <li key={i} className="text-sm text-white/85 flex items-start gap-2"><span className="text-[#F4B942] mt-0.5">→</span> {t}</li>)}</ul>
              </div>
            )}
          </div>
        </AnalisaCard>
      )}
      <PostesBarCharts items={items} />
      <div className="space-y-3">
        {ordered.map((it, i) => {
          const skor = it.skor != null ? it.skor : it.posttest;
          const benar = it.benar != null ? it.benar : (skor != null ? Math.round(skor / 10) : null);
          const salah = it.salah != null ? it.salah : (skor != null ? 10 - Math.round(skor / 10) : null);
          return (
            <div key={i} className="clay-card p-5" data-testid={`posttest-item-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h4 className="font-heading font-bold text-[#1A1C1A]">{it.mapel ? canonicalMapel(it.mapel) : "Postes"}</h4>
                <span className="text-xs text-[#5C5F5C] flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> {it.tanggal || fmtDate(it.uploaded_at)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                <div><span className="text-[#5C5F5C]">Benar: </span><b className="text-[#4A6B53]">{benar ?? "-"}</b></div>
                <div><span className="text-[#5C5F5C]">Salah: </span><b className="text-[#D9381E]">{salah ?? "-"}</b></div>
                <div><span className="text-[#5C5F5C]">Skor: </span><b className="text-[#E25E3E] text-base">{skor ?? "-"}</b></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function InAppTryouts({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mb-4" data-testid="inapp-tryouts">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E25E3E]/10 text-[#E25E3E] text-sm font-medium mb-3"><Trophy className="h-4 w-4" /> TryOut di Aplikasi</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((t, i) => (
          <div key={i} className="clay-card p-4" data-testid={`inapp-tryout-${i}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-heading font-bold text-[#1A1C1A] text-sm">{t.nama || t.kategori || "TryOut"}</p>
                <p className="text-xs text-[#9C9A94]">{fmtDate(t.created_at)}{t.kategori ? ` · ${t.kategori}` : ""}</p>
              </div>
              <div className="text-right">
                <span className="font-heading font-black text-2xl text-[#E25E3E]">{t.skor}</span>
                {t.benar != null && t.total != null && <p className="text-xs text-[#9C9A94]">{t.benar}/{t.total} benar</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
